/**
 * Homepage hero gallery sets — curated image/video slides with Publish gate.
 */
import React from "react";
import {
  buildCollection,
  useSnackbarController,
  type CollectionActionsProps,
} from "@firecms/core";
import {
  AutoAwesomeIcon,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@firecms/ui";
import { editorOrAdminPermissions } from "../permissions";
import { CONTENT_STATUS_ENUM } from "../lib/contentStatus";
import {
  STATUS_FIELD_DESCRIPTION,
  publishedSnapshotProperties,
} from "../lib/publishedSnapshotProperties";
import { collectHomeHeroSetCompletenessIssues } from "../lib/completenessValidation";
import { createStatusPreSave } from "../lib/statusPreSave";

const CONTENT_ENGINE_URL =
  (import.meta.env.VITE_CONTENT_ENGINE_URL as string | undefined)?.replace(
    /\/$/,
    "",
  ) ?? "";

const DEFAULT_KEYWORD_HINT =
  "Leave empty to auto-pick a balanced mix (nature / urban / travel).\n" +
  "Or paste your own (one per line), e.g.:\n" +
  "tokyo street rain night mood\n" +
  "swiss alps dawn mist\n" +
  "ferry deck ocean horizon";

function ProposeHomeHeroSetAction(props: CollectionActionsProps) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [keywordsText, setKeywordsText] = React.useState("");
  const [includeVideo, setIncludeVideo] = React.useState(true);
  const [bankPreview, setBankPreview] = React.useState<string>("");
  const snackbar = useSnackbarController();

  const loadBank = React.useCallback(async () => {
    if (!CONTENT_ENGINE_URL) return;
    try {
      const idToken = await props.context.authController.getAuthToken();
      if (!idToken?.trim()) return;
      const res = await fetch(`${CONTENT_ENGINE_URL}/home-hero-keyword-bank`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        nature?: string[];
        urban?: string[];
        travel?: string[];
      };
      const lines = [
        "Default bank (auto-pick mixes these):",
        `Nature: ${(data.nature ?? []).slice(0, 4).join(" · ")}…`,
        `Urban: ${(data.urban ?? []).slice(0, 4).join(" · ")}…`,
        `Travel: ${(data.travel ?? []).slice(0, 4).join(" · ")}…`,
      ];
      setBankPreview(lines.join("\n"));
    } catch {
      /* ignore */
    }
  }, [props.context.authController]);

  React.useEffect(() => {
    if (open) void loadBank();
  }, [open, loadBank]);

  const onPropose = async () => {
    if (!CONTENT_ENGINE_URL) {
      snackbar.open({
        type: "error",
        message: "VITE_CONTENT_ENGINE_URL is not configured.",
      });
      return;
    }
    setLoading(true);
    try {
      const idToken = await props.context.authController.getAuthToken();
      if (!idToken?.trim()) {
        snackbar.open({
          type: "error",
          message: "Not authenticated. Please sign in again.",
        });
        return;
      }
      const keywords = keywordsText
        .split(/[\n,]+/)
        .map((k) => k.trim())
        .filter(Boolean);
      const res = await fetch(`${CONTENT_ENGINE_URL}/propose-home-hero-set`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          include_video: includeVideo,
          ...(keywords.length ? { keywords } : {}),
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        document_id?: string;
        error?: string;
        slide_count?: number;
        keywords_used?: string[];
        warnings?: string[];
        pexels_configured?: boolean;
        video_count?: number;
      };
      if (!res.ok) {
        snackbar.open({
          type: "error",
          message: data.error ?? `Propose failed (${res.status})`,
        });
        return;
      }
      const kw = (data.keywords_used ?? []).join(", ");
      const warnParts = [...(data.warnings ?? [])];
      if (includeVideo && data.pexels_configured === false) {
        warnParts.unshift(
          "PEXELS_API_KEY is not on Content Engine Cloud Run yet — redeploy services after the secret is wired in deploy-services.",
        );
      }
      const warn = warnParts.length ? ` Warnings: ${warnParts.join(" ")}` : "";
      const vid =
        typeof data.video_count === "number"
          ? ` ${data.video_count} video(s).`
          : "";
      snackbar.open({
        type: "success",
        message:
          `Draft hero set created (${data.slide_count ?? "?"} slides,${vid} id ${
            data.document_id ?? "?"
          }). Keywords: ${kw || "(see notes)"}.${warn} Review, mark Ready, then Publish.`,
      });
      setOpen(false);
      setKeywordsText("");
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Propose failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        color="primary"
        disabled={loading}
        onClick={() => setOpen(true)}
      >
        <AutoAwesomeIcon size="small" />
        Propose next home hero set
      </Button>
      <Dialog open={open} onOpenChange={(v) => !loading && setOpen(v)}>
        <DialogTitle>Propose home hero set</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className="mb-3">
            Creates a <strong>draft</strong> only. Live homepage is unchanged until
            you mark Ready and Global Publish. One image per keyword when Unsplash
            returns geo-tagged media. With video enabled, up to{" "}
            <strong>4 Pexels motion clips</strong> are added from a separate
            place+motion bank (ranked, not first-hit). Places are unique across
            the set; country names are normalized to English.
          </Typography>
          {bankPreview
            ? (
              <Typography
                variant="caption"
                className="mb-3 block whitespace-pre-wrap text-surface-accent"
              >
                {bankPreview}
              </Typography>
            )
            : null}
          <TextField
            value={keywordsText}
            onChange={(e) => setKeywordsText(e.target.value)}
            label="Keywords (optional)"
            multiline
            minRows={4}
            placeholder={DEFAULT_KEYWORD_HINT}
            disabled={loading}
          />
          <label className="mt-4 flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={includeVideo}
              disabled={loading}
              onChange={(e) => setIncludeVideo(e.target.checked)}
            />
            <span>
              Include up to 4 Pexels motion videos (needs{" "}
              <code>PEXELS_API_KEY</code> on Content Engine Cloud Run via
              deploy-services). Uses a dedicated motion query bank, not the
              image mood keywords.
            </span>
          </label>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            disabled={loading}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            color="primary"
            disabled={loading}
            onClick={() => void onPropose()}
          >
            {loading ? "Proposing…" : "Propose draft"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function HomeHeroSetsToolbarActions(props: CollectionActionsProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 venttra-collection-toolbar-actions">
      <ProposeHomeHeroSetAction {...props} />
    </div>
  );
}

const photographerProperties = {
  name: {
    name: "Name",
    dataType: "string" as const,
  },
  username: {
    name: "Username",
    dataType: "string" as const,
  },
  url: {
    name: "Profile URL",
    dataType: "string" as const,
    url: true,
  },
};

const locationProperties = {
  name: {
    name: "Place name",
    dataType: "string" as const,
  },
  city: {
    name: "City",
    dataType: "string" as const,
  },
  country: {
    name: "Country",
    dataType: "string" as const,
  },
};

const slideProperties = {
  media_type: {
    name: "Media type",
    dataType: "string" as const,
    enumValues: [
      { id: "image", label: "Image" },
      { id: "video_sequence", label: "Video sequence" },
    ],
    description: "Image (Unsplash) or short muted video loop (Pexels hotlink).",
  },
  imageUrl: {
    name: "Image / poster URL",
    dataType: "string" as const,
    url: true,
    description:
      "Still image URL, or poster for video slides (LCP / reduced-motion).",
  },
  imageSrcset: {
    name: "Image srcset",
    dataType: "string" as const,
  },
  imageSizes: {
    name: "Image sizes",
    dataType: "string" as const,
  },
  posterUrl: {
    name: "Poster URL",
    dataType: "string" as const,
    url: true,
    description: "Optional override; defaults to imageUrl for video.",
  },
  videoUrl: {
    name: "Video URL",
    dataType: "string" as const,
    url: true,
    description:
      "Hotlinked MP4 (Pexels). Prefer re-resolving from Pexels video id on publish/build.",
  },
  pexelsVideoId: {
    name: "Pexels video ID",
    dataType: "number" as const,
  },
  pexelsFileId: {
    name: "Pexels file ID",
    dataType: "number" as const,
  },
  caption: {
    name: "Caption (place)",
    dataType: "string" as const,
    description:
      "Where this is — trip inspiration (e.g. “Lisbon, Portugal”). Required for Ready.",
  },
  location: {
    name: "Location",
    dataType: "map" as const,
    properties: locationProperties,
    description: "Structured geo when available from Unsplash / keyword hint.",
  },
  altText: {
    name: "Alt text",
    dataType: "string" as const,
  },
  credit: {
    name: "Credit name",
    dataType: "string" as const,
  },
  photographer: {
    name: "Photographer / filmmaker",
    dataType: "map" as const,
    properties: photographerProperties,
  },
  photoUrl: {
    name: "Source page URL",
    dataType: "string" as const,
    url: true,
  },
  provider: {
    name: "Provider",
    dataType: "string" as const,
    enumValues: [
      { id: "unsplash", label: "Unsplash" },
      { id: "pexels", label: "Pexels" },
      { id: "wikimedia_commons", label: "Wikimedia Commons" },
      { id: "upload", label: "Upload" },
      { id: "manual", label: "Manual" },
    ],
  },
  license: {
    name: "License",
    dataType: "string" as const,
  },
  licenseUrl: {
    name: "License URL",
    dataType: "string" as const,
    url: true,
  },
  creditLine: {
    name: "Credit line override",
    dataType: "string" as const,
  },
  focalX: {
    name: "Focal X",
    dataType: "string" as const,
  },
  focalY: {
    name: "Focal Y",
    dataType: "string" as const,
  },
  source: {
    name: "Source",
    dataType: "string" as const,
    enumValues: [
      { id: "unsplash", label: "Unsplash" },
      { id: "pexels", label: "Pexels" },
      { id: "upload", label: "Upload" },
      { id: "manual", label: "Manual" },
    ],
  },
  source_ref: {
    name: "Source ref",
    dataType: "string" as const,
    description: "Unsplash photo id, Pexels video id, or storage path.",
  },
  keyword: {
    name: "Keyword",
    dataType: "string" as const,
    description: "Mood keyword that produced this slide.",
  },
  duration_ms: {
    name: "Duration (ms)",
    dataType: "number" as const,
    description: "Carousel dwell or clip length hint.",
  },
};

export const homeHeroSetsCollection = buildCollection({
  id: "home_hero_sets",
  name: "Home Hero Sets",
  singularName: "Home Hero Set",
  path: "home_hero_sets",
  icon: "ViewCarousel",
  group: "Site",
  Actions: HomeHeroSetsToolbarActions,
  permissions: editorOrAdminPermissions,
  callbacks: {
    onPreSave: createStatusPreSave({
      collectCompleteness: collectHomeHeroSetCompletenessIssues,
      entityLabel: "home hero set",
    }),
  },
  properties: {
    title: {
      name: "Title",
      dataType: "string",
      validation: { required: true },
      description: "Editor-facing label (e.g. Spring mood landscapes).",
    },
    notes: {
      name: "Notes",
      dataType: "string",
      multiline: true,
      description:
        "Internal review notes (includes Keywords used: …). Not shown on the site.",
    },
    keywords_used: {
      name: "Keywords used",
      dataType: "array",
      of: {
        dataType: "string",
      },
      description:
        "Search keywords that produced this draft. Edit and re-propose from the toolbar if the bank missed the mark.",
    },
    slides: {
      name: "Slides",
      dataType: "array",
      of: {
        dataType: "map",
        properties: slideProperties,
      },
      description:
        "Ordered hero slides (one per keyword when proposed; up to 12). Mood imagery with place captions — not single landmarks as icons.",
    },
    status: {
      name: "Status",
      dataType: "string",
      enumValues: CONTENT_STATUS_ENUM,
      description: STATUS_FIELD_DESCRIPTION,
    },
    ...publishedSnapshotProperties,
    created_at: {
      name: "Created At",
      dataType: "date",
      autoValue: "on_create",
    },
    updated_at: {
      name: "Updated At",
      dataType: "date",
      autoValue: "on_update",
    },
  },
});
